/** @format */

/**
 * File: MeetingViewLayout.jsx
 * Version: 3.2.1
 */

import { useState, Fragment } from "@wordpress/element";
import AthleteManager from "./athletes/AthleteManager.jsx";
import RelayManager from "./relays/RelayManager.jsx";
import InlineLoginForm from "./InlineLoginForm.js";
import MeetingHeader from "./MeetingHeader.js";
import MeetingTabs from "./MeetingTabs.js";
import MeetingEntriesModal from "./MeetingEntriesModal.js";

const MeetingViewLayout = ({
	meeting,
	meetingPostId, // <-- Accept the top-level identifier prop cleanly
	isLoggedIn,
	setIsLoggedIn,
	activeTab,
	handleTabChange,
	selectedDayFilter,
	setSelectedDayFilter,
	selectedDisciplineFilter,
	setSelectedDisciplineFilter,
	trackingDisciplines,
	uniqueDays,
	masterSchedule,
	syncMeetingMetadata
}) => {
	const [registrationType, setRegistrationType] = useState("athletes");
	const [scheduleSearch, setScheduleSearch] = useState("");
	const [activeEntryView, setActiveEntryView] = useState(null);
	const [eventEntriesList, setEventEntriesList] = useState([]);
	const [entriesLoading, setEntriesLoading] = useState(false);

	const formatLongDateTime = (dateTimeStr) => {
		if(!dateTimeStr) return "—";
		try {
			return new Date(dateTimeStr).toLocaleString(undefined, {
				dateStyle: "long",
				timeStyle: "short"
			});
		} catch(e) {
			return dateTimeStr;
		}
	};

	const deadlineTime = meeting?.registrationDeadline || meeting?.deadline;
	const isDeadlinePassed = deadlineTime ? new Date().getTime() >= new Date(deadlineTime).getTime() : false;
	const deadlineContainerClass = isDeadlinePassed
		? "meet-deadline card flex-column p-3 border border-danger bg-dark text-danger h-100"
		: "meet-deadline card flex-column p-3 border border-success bg-dark text-success h-100";

	const handleOpenEntriesModal = (eventItem) => {
		setActiveEntryView(eventItem);
		setEntriesLoading(true);

		const globalData = window.ammData;
		const targetScheduleId = eventItem.id || eventItem.scheduleId;
		const entriesUrl = `${ globalData.api_url }/meetings/${ meetingPostId }/entries?schedule_id=${ targetScheduleId }`;

		fetch(entriesUrl, { headers: { "X-WP-Nonce": globalData.nonce } })
			.then((res) => res.json())
			.then((data) => {
				setEventEntriesList(Array.isArray(data) ? data : (data.entries || []));
				setEntriesLoading(false);
			})
			.catch(() => {
				setEventEntriesList([]);
				setEntriesLoading(false);
			});
	};

	// Track-and-field natural entry progression assessment rule
	const shouldShowActionButton = (item) => {
		const count = parseInt(item.totalEntries || item.total_entries || 0, 10);
		if(count <= 0) return false;

		const round = (item.phaseTypeName || item.round || "").trim().toLowerCase();

		// Special case: 4 rounds present (Qualification round allowed alongside Prelims)
		if(round === "qualification") return true;

		if(count <= 8) {
			return round === "final";
		} else if(count <= 24) {
			return round === "semi-final" || round === "semis" || round === "semi final";
		} else {
			return round === "preliminary" || round === "prelims" || round === "prelim";
		}
	};

	const isSearching = scheduleSearch.trim().length > 0;

	// Pure Functional Pipeline compiling row entries criteria
	const processedSchedule = masterSchedule
		.filter((item) => {
			// Rule: Ignore day filter matching constraints if user is performing a search query
			if(!isSearching) {
				const dayStr = `Day ${ item.dayNumber || 1 }`;
				if(dayStr !== selectedDayFilter) return false;
			}

			// Apply discipline selector parameters cleanly
			if(selectedDisciplineFilter !== "All" && (item.disciplineTypeName || item.disciplineName) !== selectedDisciplineFilter) {
				return false;
			}

			const query = scheduleSearch.toLowerCase().trim();
			if(!query) return true;

			return (
				(item.event || item.disciplineName || "").toLowerCase().includes(query) ||
				(item.ageClass || item.ageGroup || "").toLowerCase().includes(query) ||
				(item.phaseTypeName || item.round || "").toLowerCase().includes(query)
			);
		})
		.sort((a, b) => {
			if(parseInt(a.dayNumber) !== parseInt(b.dayNumber)) {
				return parseInt(a.dayNumber) - parseInt(b.dayNumber);
			}
			return (a.phaseDateAndTime || "").localeCompare(b.phaseDateAndTime || "");
		});

	if(!isLoggedIn) {
		return <InlineLoginForm onLoginSuccess={ () => { setIsLoggedIn(true); syncMeetingMetadata(); } } />;
	}

	if(!meeting) {
		return <div className="alert alert-danger">Competition schema data entity target could not be verified.</div>;
	}

	let lastRenderedDayNumber = null;

	return (
		<Fragment>
			<MeetingHeader meetingData={ meeting } formatLongDateTime={ formatLongDateTime } deadlineContainerClass={ deadlineContainerClass } isDeadlinePassed={ isDeadlinePassed } />
			<MeetingTabs activeTab={ activeTab } handleTabChange={ handleTabChange } />

			<div className="tab-content bg-white p-4 rounded-bottom border border-top-0 border-light-subtle">
				{ activeTab === "about" && (
					<div className="fade-in animate__animated animate__fadeIn">
						<div className="row">
							<div className="col-12 mb-4">
								<h4 className="fw-bold text-dark mb-2">About the Meet</h4>
								<p className="text-secondary">{ meeting.meetingDescription || "No supplemental details available for this event window." }</p>
							</div>
							<div className="col-md-6 mb-3">
								<div className="p-3 border rounded bg-light">
									<h5 className="fw-bold text-secondary mb-3"><i className="fas fa-shield-alt me-2"></i>Age Class Standards</h5>
									<p className="small text-dark mb-0">Calculated using <strong>{ meeting.ageStandard === "standard_intl" ? "International" : "JA High School" } Standard</strong>.</p>
								</div>
							</div>
						</div>
					</div>
				) }

				{ activeTab === "schedule" && (
					<div className="fade-in animate__animated animate__fadeIn">
						{/* Dropdown Filters Action Row */ }
						<div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
							<div>
								<h4 className="fw-bold text-dark mb-1">Official Event Running Orders</h4>
								<p className="text-muted small mb-0">Clickable rows indicate compiled initial declarations.</p>
							</div>
							<div className="d-flex flex-wrap align-items-center gap-2">
								<select
									className={ `form-select form-select-sm border-secondary-subtle font-weight-bold ${ isSearching ? "opacity-50 text-muted" : "" }` }
									style={ { maxWidth: "140px" } }
									value={ isSearching ? "" : selectedDayFilter }
									disabled={ isSearching }
									onChange={ (e) => setSelectedDayFilter(e.target.value) }
								>
									{ isSearching && <option value="">All Days</option> }
									{ uniqueDays.map((day) => <option key={ day } value={ day }>{ day }</option>) }
								</select>

								<select
									className="form-select form-select-sm border-secondary-subtle font-weight-bold"
									style={ { maxWidth: "180px" } }
									value={ selectedDisciplineFilter }
									onChange={ (e) => setSelectedDisciplineFilter(e.target.value) }
								>
									<option value="All">All Disciplines</option>
									{ trackingDisciplines.map((disc) => <option key={ disc } value={ disc }>{ disc }</option>) }
								</select>
							</div>
						</div>
						{/* Smart Instant Search Input Box Block */ }
						<div className="mb-4 position-relative" style={ { maxWidth: "420px" } }>
							<span className="position-absolute top-50 start-0 translate-middle-y ps-3 text-muted">
								<i className="fas fa-search small"></i>
							</span>
							<input type="text" className="form-control form-control-sm ps-5 pe-4 py-2 border-secondary-subtle rounded-3" placeholder="Instant search across all days..." value={ scheduleSearch } onChange={ (e) => setScheduleSearch(e.target.value) } />
							{ isSearching && (
								<button className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-muted text-decoration-none pe-3 py-0 border-0 shadow-none" onClick={ () => setScheduleSearch("") } style={ { background: "none" } }>
									<i className="fas fa-times-circle"></i>
								</button>
							) }
						</div>

						{/* Search Mode Alert Badge */ }
						{ isSearching && (
							<div className="alert alert-info py-2 px-3 small border-0 bg-opacity-10 rounded-2 mb-3 d-flex align-items-center gap-2">
								<i className="fas fa-info-circle"></i>
								<span>
									Showing matches across <strong>All Days</strong> of the competition schedule.
								</span>
							</div>
						) }

						{/* Schedule Output Table Block */ }
						<div className="table-responsive border rounded bg-white shadow-sm">
							<table className="table table-hover align-middle mb-0">
								<thead className="table-light text-secondary small text-uppercase">
									<tr>
										<th className="ps-4" style={ { width: "120px" } }>
											Time Slot
										</th>
										<th>Event</th>
										<th style={ { width: "130px" } }>Gender</th>
										<th style={ { width: "140px" } }>Age Group</th>
										<th style={ { width: "150px" } }>Round</th>
										<th className="pe-4 text-end" style={ { width: "150px" } }>
											Action
										</th>
									</tr>
								</thead>
								<tbody className="small">
									{ processedSchedule.length === 0 ? (
										<tr><td colSpan="6" className="text-center p-4 text-muted">No running orders matched.</td></tr>
									) : (
										processedSchedule.map((item, index) => {
											const currentItemDay = item.dayNumber || 1;
											let dayDividerRow = null;

											// Inject a sticky Day Section indicator row when browsing across all multi-day search views globally
											if(isSearching && currentItemDay !== lastRenderedDayNumber) {
												dayDividerRow = (
													<tr key={ `day-header-${ currentItemDay }` } className="table-secondary border-top border-bottom bg-light">
														<td colSpan="6" className="ps-4 py-2 fw-bold text-dark text-uppercase small tracking-wider">
															<i className="fas fa-calendar-day me-2 text-secondary"></i> Day { currentItemDay }
														</td>
													</tr>
												);
												lastRenderedDayNumber = currentItemDay;
											}

											let displayTime = "—";
											if(item.phaseDateAndTime) {
												try {
													// Adjusted hour mapping from '2-digit' to 'numeric' to securely omit arbitrary leading zero characters
													displayTime = new Date(item.phaseDateAndTime).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
												} catch {
													displayTime = item.time || "—";
												}
											}

											const renderButton = shouldShowActionButton(item);

											return (
												<Fragment key={ `item-${ item.id || index }` }>
													{ dayDividerRow }
													<tr className={ renderButton ? "cursor-pointer" : "" } onClick={ () => { if(renderButton) handleOpenEntriesModal(item); } }>
														<td className="ps-4 fw-semibold text-primary">{ displayTime }</td>
														<td className="fw-bold text-dark">{ item.disciplineName || item.event }</td>
														<td>
															{ (() => {
																const rawGender = String(item.sexCode || item.gender || "Mixed").trim().toLowerCase();
																const isMale = ["m", "male", "men", "boy"].includes(rawGender);
																const isFemale = ["f", "female", "women", "girl"].includes(rawGender);

																// Determine badge styling classes dynamically
																let badgeClass = "bg-secondary bg-opacity-10 text-secondary"; // Default for Mixed / Open
																if(isMale) badgeClass = "bg-primary bg-opacity-10 text-primary";
																if(isFemale) badgeClass = "bg-danger bg-opacity-10 text-danger";

																// Normalize display typography output text seamlessly
																let displayText = "Mixed";
																if(isMale) displayText = "Male";
																if(isFemale) displayText = "Female";

																return (
																	<span className={ `badge px-2 py-1 lh-sm fw-semibold ${ badgeClass }` }>
																		{ displayText }
																	</span>
																);
															})() }
														</td>
														<td><span className="fw-medium text-dark"> { item.ageClass || item.ageGroup || "Open" } </span></td>
														<td>
															{ (() => {
																const rawRound = String(item.phaseTypeName || item.round || "Final").trim().toLowerCase();

																// Map structural keywords cleanly to their specific color palettes
																let badgeClass = "bg-secondary bg-opacity-10 text-secondary border-secondary border-opacity-20"; // Default fallback
																let displayText = item.phaseTypeName || item.round || "Final";

																if(["final"].includes(rawRound)) {
																	// Rich Orange / Warning range
																	badgeClass = "bg-warning bg-opacity-10 text-warning-emphasis border-warning border-opacity-20";
																	displayText = "Final";
																} else if(["semis", "semi-final", "semi final"].includes(rawRound)) {
																	// Bright Amber / Deep Orange tone
																	badgeClass = "bg-orange-subtle text-orange border-orange border-opacity-10";
																	displayText = "Semis";
																} else if(["prelims", "preliminary", "prelim"].includes(rawRound)) {
																	// Vibrant Purple range
																	badgeClass = "bg-purple bg-opacity-10 text-purple border-purple border-opacity-20";
																	displayText = "Prelims";
																} else if(["qualification", "qualifying", "qual"].includes(rawRound)) {
																	// Deep Indigo / Royal Blue range
																	badgeClass = "bg-indigo bg-opacity-10 text-indigo border-indigo border-opacity-20";
																	displayText = "Qualification";
																} else if(["decathlon"].includes(rawRound)) {
																	// Teal range
																	badgeClass = "bg-teal bg-opacity-10 text-teal border-teal border-opacity-20";
																	displayText = "Decathlon";
																} else if(["heptathlon"].includes(rawRound)) {
																	// Cyan / Dark Aqua range
																	badgeClass = "bg-info bg-opacity-10 text-info-emphasis border-info border-opacity-20";
																	displayText = "Heptathlon";
																} else if(["octathlon"].includes(rawRound)) {
																	// Fresh Green / Success range
																	badgeClass = "bg-success bg-opacity-10 text-success border-success border-opacity-20";
																	displayText = "Octathlon";
																} else if(["international"].includes(rawRound)) {
																	// Magenta / Pink range for elite context showcases
																	badgeClass = "bg-pink bg-opacity-10 text-pink border-pink border-opacity-20";
																	displayText = "International";
																}

																return (
																	<span className={ `badge rounded-3 border lh-sm fw-semibold px-2 py-1 ${ badgeClass }` }>
																		{ displayText }
																	</span>
																);
															})() }
														</td>														<td className="pe-4 text-end">
															{ renderButton ? (
																<button className="btn btn-sm btn-primary px-2 rounded-2 fw-bold d-inline-flex align-items-center gap-2 shadow-sm" onClick={ (e) => { e.stopPropagation(); handleOpenEntriesModal(item); } }><i className="fas fa-clipboard-list small"></i> Entries</button>
															) : (
																<span className="text-muted small fst-italic">—</span>
															) }
														</td>
													</tr>
												</Fragment>
											);
										})
									) }
								</tbody>
							</table>
						</div>
					</div>
				) }

				{ activeTab === "register" && (
					<Fragment>
						<div className="d-flex gap-2 mb-4 bg-light p-2 rounded border" style={ { maxWidth: "fit-content" } }>
							<button className={ `btn btn-sm px-4 fw-bold ${ registrationType === "athletes" ? "btn-primary shadow-sm" : "btn-light text-secondary" }` } onClick={ () => setRegistrationType("athletes") }><i className="fas fa-user me-2"></i>Individual Athletes</button>
							<button className={ `btn btn-sm px-4 fw-bold ${ registrationType === "relays" ? "btn-primary shadow-sm" : "btn-light text-secondary" }` } onClick={ () => setRegistrationType("relays") }><i className="fas fa-users me-2"></i>Relay Teams</button>
						</div>

						{ registrationType === "athletes" ? (
							<AthleteManager meetingPostId={ meetingPostId } ageStandard={ meeting.ageStandard || "standard_intl" } registrationDeadline={ deadlineTime } onDataMutation={ syncMeetingMetadata } />
						) : (
							<RelayManager meetingPostId={ meetingPostId } ageStandard={ meeting.ageStandard || "standard_intl" } registrationDeadline={ deadlineTime } onDataMutation={ syncMeetingMetadata } meetingSchedule={ meeting.schedule || [] } />
						) }
					</Fragment>
				) }
			</div>

			{ activeEntryView && (
				<MeetingEntriesModal activeEntryView={ activeEntryView } onClose={ () => setActiveEntryView(null) } getRoundDisclaimerText={ () => `Showing ${ eventEntriesList.length } declared configurations.` } entriesLoading={ entriesLoading } eventEntriesList={ eventEntriesList } />
			) }
		</Fragment>
	);
};

export default MeetingViewLayout;